import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import confetti from 'canvas-confetti';
import { lastValueFrom, take, takeWhile, timer } from 'rxjs';
import { Evaluation, TestResult } from '../../interfaces/questionInterface';
import { QuestionService } from '../../services/question.service';
import { TestService } from '../../services/test.service';
import { AnswerComponent } from '../answer/answer.component';
import { AchievementService } from '../../services/achievement.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { Achievement } from '../../interfaces/achievementInterface';
import { PocketbaseService } from '../../services/pocketbase.service';

@Component({
    selector: 'app-evaluation-overview',
    imports: [AnswerComponent, DatePipe, CommonModule, RouterLink, ModalComponent],
    templateUrl: './evaluation-overview.component.html',
    styleUrl: './evaluation-overview.component.scss'
})
export class EvaluationOverviewComponent {
  @ViewChild('fadediv', { read: ElementRef })
  fadediv!: ElementRef<HTMLDivElement>;

  public testId: string = '';
  public examSimName: string = '';
  public timeTaken: number = 0;
  public score: number = 0;
  public treshold: number = 0;
  public evaluations: Evaluation[] = [];

  public testResult?: TestResult = undefined;
  public flaggedQuestions: string[] = [];
  public bookmarkedQuestions: string[] = [];

  public achievement?: {achievement: Achievement, nextCount?: number};

  //for the filtering
  buttonStates: boolean[] = [true, false, false, false];
  @ViewChildren(AnswerComponent) AnswerComponentList?: QueryList<AnswerComponent>;
  public renderedComponentCounts: { [key: string]: number } = {
    none: 0,
    some: 0,
    all: 0,
    flagged: 0,
  };

  @ViewChild('achievementModal')
  achievementModal!: ModalComponent;
  courseId?: string;

  constructor(
    private testService: TestService,
    private route: ActivatedRoute,
    private questionService: QuestionService,
    private achievementService: AchievementService,
    private pocketbaseService: PocketbaseService
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id !== null) {
        this.testId = id;
      } else {
        // when id not found
      }
    });


    this.testService.getTestResults(this.testId).then((data) => {
      this.testResult = data;
      this.examSimName = data.test.expand.conditions.name;
      this.timeTaken = data.time_taken;
      this.score = data.score;
      this.treshold = data.test.expand.conditions.threshold;
      this.pocketbaseService.pb.collection('courses').getFirstListItem(this.pocketbaseService.pb.filter('exam = {:examId}', {examId: data.test.expand.conditions.id})).then(course => {
        this.courseId = course.id;
      });

      if (this.score >= this.treshold) {
        this.playFireworks();
        this.tryClaimAchievement();
      }

      for (let evalu = 0; evalu < data.evaluations.length; evalu++) {
        this.evaluations.push(data.evaluations[evalu].evaluation);
      }
    });

    this.getFlaggedQuestions();
    this.getBookmarkedQuestions();
  }

  ngAfterViewInit() {
    this.AnswerComponentList?.changes.subscribe(() => {
      setTimeout(() => {
        //so it uses function before the page has been rendered completely
        this.computeAnswerComponentCorrectTypes();
      });
    });
  }

  //changes clicked button into the active one and the other ones into false state, also goes back to first index when clicked again
  setActiveButton(buttonIndex: number): void {
  if (this.buttonStates[buttonIndex]) { // if active, set the first button to true and others to false
    this.buttonStates = this.buttonStates.map((state, index) => index === 0);
  } else { // if not active, toggle the clicked button and set others to false
    this.buttonStates = this.buttonStates.map((state, index) => index === buttonIndex);
  }
  }

  //when clicking on Button 'Übersicht'
  async showAllAnswerComponents() {
    await this.fadeOut();
    if (this.AnswerComponentList) {
      this.AnswerComponentList.forEach((answerComponent: AnswerComponent) => {
        answerComponent.show();
      });
    }
    this.fadeIn();
  }

  // for the filter buttons to only display the AnswerComponents that are expected; none and some are shown together
  async filterByCorrect(correct: boolean) {
    await this.fadeOut();
    if (this.AnswerComponentList) {
      this.AnswerComponentList.forEach((answerComponent: AnswerComponent) => {
        if (!correct) {
          if (
            answerComponent.correct === 'none' ||
            answerComponent.correct === 'some'
          ) {
            answerComponent.show();
          } else {
            answerComponent.hide();
          }
        } else {
          if (answerComponent.correct === 'all') {
            answerComponent.show();
          } else {
            answerComponent.hide();
          }
        }
      });
    }
    this.fadeIn();
  }

  async filterByFlagged() {
    await this.fadeOut();
    if (this.AnswerComponentList) {
      this.AnswerComponentList.forEach((answerComponent: AnswerComponent) => {
        if (answerComponent.flagged) {
          answerComponent.show();
        } else {
          answerComponent.hide();
        }
      })
    }
    this.fadeIn();
  }

  public async getFlaggedQuestions() {
    this.flaggedQuestions = await this.questionService.getFlaggedQuestions(this.testId);
  }

  public async getBookmarkedQuestions() {
    this.questionService.getBookmarkedQuestions().then(bookmarkedQuestions => {
      this.bookmarkedQuestions = bookmarkedQuestions
        .filter(q => q.expand != undefined)
        .map(q => q.expand!.answered_question.question);
    })
  }

  //counts how many components have either correct = none | some | all to show on the switching buttons
  private computeAnswerComponentCorrectTypes(): void {
    let noneCount = 0;
    let someCount = 0;
    let allCount = 0;
    let flagCount = 0;

    if (this.AnswerComponentList) {
      this.AnswerComponentList.forEach((answerComponent: AnswerComponent) => {
        if (answerComponent.correct === 'none') {
          noneCount++;
        } else if (answerComponent.correct === 'some') {
          someCount++;
        } else if (answerComponent.correct === 'all') {
          allCount++;
        }
        if (answerComponent.flagged) {
          flagCount++
        }
      });
    }

    this.renderedComponentCounts = {
      none: noneCount,
      some: someCount,
      all: allCount,
      flagged: flagCount
    };
  }

  isQuestionFlagged(id: string): boolean {
    return this.flaggedQuestions.includes(id);
  }

  isQuestionBookmarked(id: string): boolean {
    return this.bookmarkedQuestions.includes(id);
  }

  async tryClaimAchievement() {
    if (this.testResult) {
      const achievement = await this.achievementService.claimTestCompletionAchievement(this.testResult.test.expand.conditions.id);
      if (achievement?.achievement) {
        this.achievement = achievement;
        this.toggleAchievementModal();
      }
    }
  }

  toggleAchievementModal() {
    this.achievementModal.toggleModal();
  }
  
  async fadeOut() {
    this.fadediv.nativeElement.style.opacity = '0';
    // wait for fade out animation
    await lastValueFrom(timer(500).pipe(take(1)));
  }

  fadeIn() {
    this.fadediv.nativeElement.style.opacity = '100';
  }

  playFireworks() {
    let duration = 3500;
    let animationEnd = Date.now() + duration;
    let defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    // Start the timer immediately (0ms delay), emitting every 250ms
    const interval$ = timer(0, 250).pipe(
      takeWhile(() => Date.now() < animationEnd)
    );

    const subscription = interval$.subscribe(() => {
      let timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        subscription.unsubscribe();
        return;
      }

      let particleCount = 50 * (timeLeft / duration);
      // Perform the same actions as before
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    });
  }
}
